<template>
  <div>
    <b-card>
      <div class="d-flex flex-wrap align-items-start justify-content-between mb-2">
        <TitleTable titleTable="GLP-1 Visits" iconTable="fas fa-calendar-check" />
        <b-button
          variant="outline-success"
          size="sm"
          class="mt-2 mt-md-0"
          :disabled="loading || exporting"
          @click="exportCsv">
          <i class="fas fa-file-csv mr-1"></i>
          {{ exporting ? 'Exporting…' : 'Export CSV' }}
        </b-button>
      </div>
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="From" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_from" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="To" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_to" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Provider ID" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input
              v-model="filters.provider_id"
              type="number"
              placeholder="All providers"
              @keyup.enter="load(1)" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Patient ID" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input
              v-model="filters.weightloss_patient_id"
              type="number"
              placeholder="All patients"
              @keyup.enter="load(1)" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="normalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="facilityOptionsLoading || loading"
            @click="load(1)">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="d-flex justify-content-end align-items-center pt-2 border-top">
          <b-button variant="primary" @click="registerModalVisible = true">
            Register visit
          </b-button>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No visits found"
        class="wl-visits-table">
        <template #cell(visit_date)="data">
          {{ formatDate(data.value) }}
        </template>
        <template #cell(patient)="data">
          <div class="font-weight-bold">{{ patientName(data.item) }}</div>
          <small v-if="patientPhone(data.item)" class="text-muted d-block">
            {{ patientPhone(data.item) }}
          </small>
        </template>
        <template #cell(dose)="data">
          {{ doseLabel(data.item) }}
        </template>
        <template #cell(facility)="data">
          {{ facilityName(data.item) }}
        </template>
        <template #cell(provider)="data">
          {{ providerName(data.item) }}
        </template>
        <template #cell(weight)="data">
          {{ formatNumber(data.value, 'kg') }}
        </template>
        <template #cell(bmi)="data">
          {{ formatNumber(data.value) }}
        </template>
        <template #cell(price_charged)="data">
          {{ formatCurrency(data.value) }}
        </template>
        <template #cell(injection_given)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Yes' : 'No' }}
          </b-badge>
        </template>
        <template #cell(addons)="data">
          <span :class="{ 'text-muted': !addonCount(data.item) }">
            {{ addonSummary(data.item) }}
          </span>
        </template>
        <template #cell(notes)="data">
          <span
            v-if="data.value"
            v-b-tooltip.hover
            :title="data.value"
            class="wl-notes-cell">
            {{ truncate(data.value, 40) }}
          </span>
          <span v-else class="text-muted font-italic">—</span>
        </template>
      </b-table>

      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="{ page: currentPage }"
        @page-changed="load" />
    </b-card>
    <RegisterWlVisitModal
      :visible.sync="registerModalVisible"
      @registered="onVisitRegistered"
      @error="onRegisterError" />

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BFormGroup,
  BFormInput,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import PaginationTable from '@/components/PaginationTable'
import ToastNotification from '@/components/ToastNotification'
import wlAdminMixin from '../mixins/wlAdminMixin'
import wlLaneMixin from '../mixins/wlLaneMixin'
import RegisterWlVisitModal from './components/RegisterWlVisitModal.vue'
import axiosF from '@/core/services/api/admin/facilities'
import { exportRowsToCsv, fetchAllPaginatedPages, csvFilename } from '../utils/exportCsv'

export default {
  name: 'TableWlVisits',
  mixins: [wlAdminMixin, wlLaneMixin],
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BFormGroup,
    BFormInput,
    BBadge,
    Treeselect,
    TitleTable,
    PaginationTable,
    ToastNotification,
    RegisterWlVisitModal,
  },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      fields: [
        { key: 'id', label: 'ID', thStyle: { width: '4%' } },
        { key: 'visit_date', label: 'Date', sortable: true },
        { key: 'patient', label: 'Patient' },
        { key: 'dose', label: 'Dose' },
        { key: 'qty_doses', label: 'Qty' },
        { key: 'facility', label: 'Facility' },
        { key: 'provider', label: 'Provider' },
        { key: 'weight', label: 'Weight' },
        { key: 'bmi', label: 'BMI' },
        { key: 'price_charged', label: 'Charged' },
        { key: 'injection_given', label: 'Injection' },
        { key: 'addons', label: 'Add-ons' },
        { key: 'notes', label: 'Notes' },
      ],
      filters: {
        date_from: '',
        date_to: '',
        // All facilities by default (omit facilities_id)
        facilities_id: [],
        provider_id: '',
        weightloss_patient_id: '',
      },
      facilityOptions: [],
      facilityOptionsLoading: false,
      registerModalVisible: false,
      exporting: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      await this.loadFacilities()
      this.load(1)
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
        if (this.$refs.toast) {
          this.$refs.toast.danger((e && e.message) || 'Could not load facilities')
        }
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    buildListParams() {
      return this.buildListQueryParams({
        date_from: this.filters.date_from || null,
        date_to: this.filters.date_to || null,
        facilities_id: this.filters.facilities_id,
        provider_id: this.filters.provider_id || null,
        weightloss_patient_id: this.filters.weightloss_patient_id || null,
      })
    },
    formatDate(value) {
      if (!value) return '—'
      return String(value).slice(0, 10)
    },
    formatNumber(value, suffix = '') {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      const text = Number.isNaN(n) ? value : n.toFixed(2).replace(/\.?0+$/, '')
      return suffix ? `${text} ${suffix}` : text
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (Number.isNaN(n)) return value
      return `$${n.toFixed(2)}`
    },
    truncate(text, max) {
      const s = String(text)
      return s.length > max ? `${s.slice(0, max)}...` : s
    },
    patientPhone(item) {
      const p = item && item.patient
      return (p && p.phone) || ''
    },
    patientName(item) {
      const p = item && item.patient
      if (p && (p.first_name || p.last_name)) {
        return `${p.first_name || ''} ${p.last_name || ''}`.trim()
      }
      return item && item.weightloss_patient_id ? `#${item.weightloss_patient_id}` : '—'
    },
    doseLabel(item) {
      if (item && item.dose && item.dose.dose_label) return item.dose.dose_label
      return item && item.weightloss_dose_id ? `#${item.weightloss_dose_id}` : '—'
    },
    facilityName(item) {
      if (item && item.facility && item.facility.name) return item.facility.name
      const id = item && item.facility_id
      if (id != null) {
        const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
        return f ? f.name : `#${id}`
      }
      return '—'
    },
    providerName(item) {
      const pr = item && item.provider
      if (pr && (pr.name || pr.lastname)) {
        return `${pr.name || ''} ${pr.lastname || ''}`.trim()
      }
      return item && item.provider_id ? `#${item.provider_id}` : '—'
    },
    addonCount(item) {
      return item && Array.isArray(item.addons) ? item.addons.length : 0
    },
    addonSummary(item) {
      const count = this.addonCount(item)
      if (!count) return '—'
      const names = item.addons
        .map(a => a.description || a.name || (a.addon && a.addon.description))
        .filter(Boolean)
      if (names.length) return names.join(', ')
      return `${count} add-on${count === 1 ? '' : 's'}`
    },
    onVisitRegistered() {
      this.$refs.toast.success('Visit registered')
      this.load(this.currentPage)
    },
    onRegisterError(message) {
      this.$refs.toast.danger(message)
    },
    async load(page = 1) {
      this.loading = true
      try {
        const res = await this.wlVisitsApi.listPaginated(
          this.perPage,
          this.buildListParams(),
          page
        )
        this.applyPagination(res)
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.loading = false
      }
    },
    async exportCsv() {
      this.exporting = true
      try {
        const rows = await fetchAllPaginatedPages((perPage, page) =>
          this.wlVisitsApi.listPaginated(perPage, this.buildListParams(), page)
        )
        if (!rows.length) {
          if (this.$refs.toast) this.$refs.toast.warning('No visits to export')
          return
        }
        exportRowsToCsv(
          rows,
          [
            { key: 'id', label: 'ID' },
            {
              key: 'visit_date',
              label: 'Date',
              value: row => this.formatDate(row.visit_date),
            },
            { key: 'patient', label: 'Patient', value: row => this.patientName(row) },
            {
              key: 'phone',
              label: 'Phone',
              value: row => this.patientPhone(row),
            },
            { key: 'dose', label: 'Dose', value: row => this.doseLabel(row) },
            { key: 'qty_doses', label: 'Qty' },
            {
              key: 'facility',
              label: 'Facility',
              value: row => this.facilityName(row),
            },
            {
              key: 'provider',
              label: 'Provider',
              value: row => this.providerName(row),
            },
            { key: 'weight', label: 'Weight' },
            { key: 'bmi', label: 'BMI' },
            { key: 'price_charged', label: 'Charged' },
            {
              key: 'injection_given',
              label: 'Injection',
              value: row => (row.injection_given ? 'Yes' : 'No'),
            },
            {
              key: 'addons',
              label: 'Add-ons',
              value: row => this.addonSummary(row),
            },
            { key: 'notes', label: 'Notes', value: row => row.notes || '' },
          ],
          csvFilename('wl_visits')
        )
      } catch (e) {
        if (this.$refs.toast) {
          this.$refs.toast.danger((e && e.message) || 'Could not export CSV')
        }
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}

.wl-notes-cell {
  display: inline-block;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
